import { onUnmounted } from "vue";

let activePinStop: null | (() => void) = null;

export function useScrollPinning() {
    function easeInOutCubic(t: number) {
        return t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3)/2;
    }

    async function smoothScrollTo(topTarget: number, duration = 700) {
        const start = performance.now();
        const startY = window.scrollY;
        const delta = topTarget - startY;

        return new Promise<void>((resolve) => {
            function step(now: number) {
                const t = Math.min(1, (now - start) / duration);
                const eased = easeInOutCubic(t);
                window.scrollTo({ top: startY + delta * eased, behavior: 'auto' });
                if (t < 1) { requestAnimationFrame(step); }
                else { resolve(); }
            }
            requestAnimationFrame(step);
        });
    }

    function stopPin() {
        if (activePinStop) {
            try { activePinStop(); } catch {}
            activePinStop = null;
        }
    }

    function readCssPx(containerSelector: string, varName: string, fallback = 200) {
        const el = document.querySelector(containerSelector) as HTMLElement | null;
        const raw = el ? getComputedStyle(el).getPropertyValue(varName).trim() : "";
        const px = Number.parseInt(raw, 10);
        return Number.isFinite(px) ? px : fallback;
    }

    function pinElementDuring(
        el: HTMLElement,
        targetOffsetPx: number,
        maxMs = 2000
    ) {
        stopPin();

        const start = performance.now();
        let raf = 0;
        let stopped = false;

        let dragStartY = 0;
        let dragActive = false;
        const DRAG_THRESHOLD = 5;

        const stop = () => {
            if (stopped) { return; }
            
            stopped = true;
            cancelAnimationFrame(raf);
            cleanup();
            if (activePinStop === stop) { activePinStop = null; }
        };

        const userBreak = () => stop();
        const keyBreak = (e: KeyboardEvent) => {
            if (['ArrowUp','ArrowDown','ArrowLeft','ArrowRight','PageUp','PageDown','Home','End',' '].includes(e.key)) {
                stop();
            }
        };
        const clickBreak = (e: Event) => {
            const t = e.target as Element | null;
            if (t && (t as any).closest && (t as any).closest('.epoch__title')) { stop(); }
        };

        const pointerDown = (e: PointerEvent) => {
            dragStartY = e.clientY;
            dragActive = true;
        };

        const pointerMove = (e: PointerEvent) => {
            if (!dragActive) { return; }
            
            const diff = Math.abs(e.clientY - dragStartY);
            if (diff > DRAG_THRESHOLD) {
                stop(); // stop scroll-lock if drag
            }
        };

        const pointerUp = () => { dragActive = false; };

        const listeners: Array<() => void> = [];
        const add = (t: string, h: any, opts?: AddEventListenerOptions) => {
            window.addEventListener(t as any, h, opts);
            listeners.push(() => window.removeEventListener(t as any, h, opts as any));
        };
        const addDoc = (t: string, h: any, opts?: AddEventListenerOptions) => {
            document.addEventListener(t as any, h, opts);
            listeners.push(() => document.removeEventListener(t as any, h, opts as any));
        };

        add('wheel', userBreak, { passive: true, capture: true });
        add('touchstart', userBreak, { passive: true, capture: true });
        add('keydown', keyBreak, { capture: true });
        addDoc('click', clickBreak, { capture: true });

        add('pointerdown', pointerDown, { passive: true, capture: true });
        add('pointermove', pointerMove, { passive: true, capture: true });
        add('pointerup', pointerUp, { passive: true, capture: true });

        const cleanup = () => { listeners.forEach(off => off()); };

        const tick = () => {
            if (stopped) { return; }
            
            const now = performance.now();
            const rect = el.getBoundingClientRect();
            const delta = rect.top - targetOffsetPx;

            if (Math.abs(delta) > 0.5) {
                window.scrollBy({ top: delta * 0.25, left: 0, behavior: 'auto' });
            }

            if (now - start > maxMs) {
                stop();
                return;
            }
            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);
        activePinStop = stop;
        return stop;
    }

    onUnmounted(stopPin);

    return { smoothScrollTo, pinElementDuring, stopPin, readCssPx };
}
